import React, { useState, useCallback, memo } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGithub,
  faLinkedin,
  faXTwitter,
} from "@fortawesome/free-brands-svg-icons";
import { faFileLines } from "@fortawesome/free-solid-svg-icons";
import { useDarkMode } from "./hooks/useDarkMode";
import { useScrollReveal } from "./hooks/useScrollReveal";
import DarkModeToggle from "./components/DarkModeToggle";

import ProfilePicture from "./img/profile-picture.JPG";

import Meta from "./img/companies/meta.png";
import Amazon from "./img/companies/amazon.png";
import GovernmentOfCanada from "./img/companies/government-of-canada.png";
import UBC from "./img/companies/ubc.png";
import Optum from "./img/companies/optum.png";
import PortonHealth from "./img/companies/porton-health.png";

import forum from "./img/projects/forum.png";
import portfolioDiversificationModel from "./img/projects/portfolio-diversification-model.png";
import labby from "./img/projects/labby.png";
import postureFix from "./img/projects/posture-fix.png";

import tofinoCover from "./img/travel/tofino/IMG_2353.JPG";
import sanFranciscoCover from "./img/travel/san_francisco/IMG_1928.JPG";
import peruCover from "./img/travel/peru/DSC00228.JPG";
import austriaCover from "./img/travel/austria/IMG_7777.JPG";
import pragueCover from "./img/travel/prague/IMG_7555.JPG";

const ProfileHeader = ({ profilePicture }) => {
  const [ref, isVisible] = useScrollReveal();

  return (
    <div
      ref={ref}
      className={`flex flex-col md:flex-row items-center justify-between mb-8 ${
        isVisible ? "animate-fade-in" : "opacity-0"
      }`}
    >
      <div className="flex flex-col md:flex-row items-center">
        <img
          src={profilePicture}
          alt="Profile"
          className="w-[200px] h-[200px] md:w-[300px] md:h-[300px] rounded-full mb-4 md:mb-0 md:mr-8 object-cover shadow-xl dark:shadow-2xl dark:shadow-gray-700"
        />
        <div className="text-center md:text-left">
          <h1 className="text-4xl md:text-5xl font-bold mb-2 dark:text-white">
            Arman Moztarzadeh
          </h1>
          <h2 className="text-md italic text-gray-500 dark:text-gray-400">
            Email: me@armanmoztarzadeh.com
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-4">
            software engineer, student, and outdoor enthusiast
          </p>
          <div className="flex justify-center md:justify-start space-x-4">
            <a
              href="https://github.com/armanmoztar"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub"
              className="transform hover:scale-110 transition-transform duration-300 dark:text-white"
            >
              <FontAwesomeIcon icon={faGithub} size="2x" />
            </a>
            <a
              href="https://www.linkedin.com/in/arman-moztarzadeh/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="transform hover:scale-110 transition-transform duration-300 dark:text-white"
            >
              <FontAwesomeIcon icon={faLinkedin} size="2x" />
            </a>
            <a
              href="https://x.com/armanmoztar"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="X"
              className="transform hover:scale-110 transition-transform duration-300 dark:text-white"
            >
              <FontAwesomeIcon icon={faXTwitter} size="2x" />
            </a>
            <a
              href="/assets/AMoztarzadeh_Resume.pdf"
              target="_blank"
              download
              aria-label="Download Resume"
              className="transform hover:scale-110 transition-transform duration-300 dark:text-white"
            >
              <FontAwesomeIcon icon={faFileLines} size="2x" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

const AboutMe = () => {
  const [ref, isVisible] = useScrollReveal();

  return (
    <div
      ref={ref}
      className={`bg-white dark:bg-gray-800 rounded-lg p-6 mb-8 shadow-md text-left transition-all duration-500 ${
        isVisible ? "animate-slide-up" : "opacity-0"
      }`}
    >
      <h2 className="text-2xl font-bold mb-4 dark:text-white">About Me</h2>
      <div className="flex flex-wrap gap-2 mb-4">
        <span className="bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 px-2 py-1 rounded">
          Software Development
        </span>
        <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
          Database Systems
        </span>
        <span className="bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 px-2 py-1 rounded">
          Cloud Development
        </span>
      </div>
      <p className="text-gray-700 dark:text-gray-300">
        Hi! My name is Arman. I'm an undergraduate student at the University of
        British Columbia studying mathematics and data science.
        <br></br>
        <br></br>I love running and hiking, especially discovering new places in
        the beauties of British Columbia. One of my main goals for 2026 is to be
        able to run the full BMO marathon in May. I'm also an avid skier and
        enjoy spending time on the slopes during the winter months.
        <br></br>
        <br></br>
        Lastly, I enjoy playing the piano and guitar in my free time. You can
        see some of my old piano videos{" "}
        <a
          href="https://www.youtube.com/watch?v=EF9zPU_6sdI"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline"
        >
          here
        </a>
        .
      </p>
    </div>
  );
};

const travelPreviewItems = [
  {
    title: "Tofino",
    note: "February 2026",
    image: tofinoCover,
  },
  {
    title: "San Francisco",
    note: "January 2026",
    image: sanFranciscoCover,
  },
  {
    title: "Peru",
    note: "August 2025",
    image: peruCover,
  },
  {
    title: "Austria",
    note: "July 2025",
    image: austriaCover,
  },
  {
    title: "Prague",
    note: "July 2025",
    image: pragueCover,
  },
];

// Memoized card component to prevent unnecessary re-renders
const TravelPreviewCard = memo(({ item, index }) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  return (
    <div
      className="rounded-lg border border-gray-200 dark:border-gray-700 p-5 bg-gradient-to-br from-amber-50 via-white to-sky-50 dark:from-gray-700 dark:via-gray-800 dark:to-gray-900 card-hover"
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <div className="relative w-full h-48 rounded-md mb-4 overflow-hidden bg-gray-200 dark:bg-gray-700">
        {!imageLoaded && (
          <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 animate-pulse" />
        )}
        <img
          src={item.image}
          alt={`${item.title} travel preview`}
          className={`w-full h-48 object-cover rounded-md transition-opacity duration-300 ${
            imageLoaded ? "opacity-100" : "opacity-0"
          }`}
          loading="lazy"
          decoding="async"
          onLoad={handleImageLoad}
        />
      </div>
      <h3 className="font-semibold text-lg dark:text-white">{item.title}</h3>
      <p className="text-gray-600 dark:text-gray-400 text-sm">{item.note}</p>
    </div>
  );
});

TravelPreviewCard.displayName = "TravelPreviewCard";

const TravelPreview = () => {
  const [ref, isVisible] = useScrollReveal();

  return (
    <div
      ref={ref}
      className={`bg-white dark:bg-gray-800 rounded-lg p-6 mb-8 shadow-md text-left transition-all duration-500 ${
        isVisible ? "animate-slide-up" : "opacity-0"
      }`}
    >
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
        <div>
          <h2 className="text-2xl font-bold dark:text-white">Travel Photos</h2>
          <p className="text-gray-600 dark:text-gray-400">
            A few recent adventures with friends and family.
          </p>
        </div>
        <Link
          to="/travel"
          className="inline-flex items-center px-4 py-2 rounded-full bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200 btn-hover"
        >
          View the gallery
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {travelPreviewItems.map((item, index) => (
          <TravelPreviewCard key={index} item={item} index={index} />
        ))}
      </div>
    </div>
  );
};

const companies = [
  {
    name: "Meta",
    logo: Meta,
  },
  {
    name: "Amazon",
    logo: Amazon,
  },
  {
    name: "Government of Canada",
    logo: GovernmentOfCanada,
  },
  {
    name: "UBC",
    logo: UBC,
  },
  {
    name: "Optum",
    logo: Optum,
  },
  {
    name: "Porton Health",
    logo: PortonHealth,
  },
];

const companyLogos = companies.reduce((acc, { name, logo }) => {
  acc[name] = logo;
  return acc;
}, {});

const ExperienceItem = ({ company, role, period, responsibilities }) => (
  <div className="mb-6 text-left">
    <div className="flex items-center mb-2">
      {companyLogos[company] && (
        <img
          src={companyLogos[company]}
          alt={company}
          className="h-[24px] mr-4 object-cover dark:brightness-90"
        />
      )}
      <div>
        <h3 className="font-semibold dark:text-white">
          {role} @ {company}
        </h3>
        <p className="text-gray-600 dark:text-gray-400">{period}</p>
      </div>
    </div>
    <ul className="list-disc pl-4 text-gray-700 dark:text-gray-300">
      {responsibilities.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  </div>
);

const Experience = () => {
  const [ref, isVisible] = useScrollReveal();

  return (
    <div
      ref={ref}
      className={`bg-white dark:bg-gray-800 rounded-lg p-6 mb-8 shadow-md text-left transition-all duration-500 ${
        isVisible ? "animate-slide-up" : "opacity-0"
      }`}
    >
      <h2 className="text-xl font-bold mb-4 dark:text-white">Experiences</h2>
      <ExperienceItem
        company="Meta"
        role="Production Engineer Intern | MLH Fellow"
        period="June 2025 - September 2025"
        responsibilities={["Fellowship through MLH | Summer 2025"]}
      />
      <ExperienceItem
        company="Amazon"
        role="Software Engineer Intern"
        period="May 2025 - July 2025"
        responsibilities={[
          "Designed and implemented a Java-based backend model and schema for a REST API supporting scalable Query Promotions services for Amazon Business.",
          "Developed a serverless Cross-Border Promotions query workflow for Amazon Business using AWS Lambda and API Gateway, reducing query latency by 70%.",
          "Built a new frontend React component to display query results and extract data to CSV format using React and TypeScript, improving usability for 100+ internal stakeholders.",
        ]}
      />
      <ExperienceItem
        company="Amazon"
        role="Software Engineer Intern"
        period="June 2024 - September 2024"
        responsibilities={[
          "Optimized a React-based global dashboard used in UK retail checkout flows, improving payment security by 20% across high-traffic checkout paths.",
          "Implemented a checkout use case in TypeScript for the team's testing sandbox using AWS SAM and AWS Lambda, enabling automated testing across 15+ input scenarios and reducing manual QA time by 30%.",
          "Spearheaded the design and implementation of 2 REST APIs to handle payment authorization within a cloud-native architecture, enabling seamless experimentation with minimal disruption to the existing codebase.",
        ]}
      />
      <ExperienceItem
        company="Government of Canada"
        role="Software Engineer Intern"
        period="Jan 2024 - Apr 2024"
        responsibilities={[
          "Led the development of 7 features using Python and MongoDB, improving application functionality and supporting a major infrastructure upgrade.",
          "Wrote optimized MongoDB aggregation queries with indexing, processing collections of over 5 million documents and significantly improving query performance in critical admin dashboards.",
          "Created detailed Confluence documentation related to development, facilitating future employee onboarding.",
        ]}
      />
      <ExperienceItem
        company="UBC"
        role="Undergraduate Teaching Assistant"
        period="Sep 2023 - Apr 2024"
        responsibilities={[
          "Designed and developed an AWS EMR performance benchmarking assignment comparing single-node and multi-node clusters for large-scale data processing, analyzing cluster optimization trade-offs.",
          "Reviewed and provided technical feedback on assignments involving AWS, Azure, and Docker, reinforcing best practices for cloud-native and distributed systems.",
          "Supported 100+ computer science and data science students in understanding cloud computing and distributed systems concepts.",
        ]}
      />
      <ExperienceItem
        company="Optum"
        role="Software Engineer in Test Co-op"
        period="May 2023 - Dec 2023"
        responsibilities={[
          "Collaborated closely with the testing team to develop and execute advanced automated test scripts using C# and Selenium, leading to a 10% performance enhancement as measured by LoadRunner benchmarks.",
          "Leveraged VMware and Google Cloud platforms to architect and manage virtual testing environments.",
          "Developed 8 Python scripts utilizing pandas and numpy to automate critical tasks including extracting archive log data and generating CSV reports.",
        ]}
      />
      <ExperienceItem
        company="Porton Health"
        role="Software Engineer Intern"
        period="Sep 2022 - Dec 2022"
        responsibilities={[
          "Secured existing web applications by integrating user authentication features utilizing JavaScript and Node.js, reducing unauthorized access attempts by 40% and improving application security.",
          "Tested and debugged over 35 components using MongoDB and documented workflow.",
          "Collaborated with UI designers and senior engineers to develop and deploy production-ready email templates for a healthcare platform.",
        ]}
      />
    </div>
  );
};

const ProjectCard = ({ project, onClick }) => (
  <div
    className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden cursor-pointer card-hover border border-gray-200 dark:border-gray-700"
    onClick={onClick}
  >
    <img
      src={project.image}
      alt={project.title}
      className="w-full h-48 object-cover"
    />
    <div className="p-4">
      <h3 className="font-bold text-lg mb-2 dark:text-white">
        {project.title}
      </h3>
      <p className="text-gray-600 dark:text-gray-400">
        {project.shortDescription}
      </p>
    </div>
  </div>
);

const ProjectModal = ({ project, onClose }) => {
  const handleOutsideClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose],
  );

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex justify-center items-center z-50 text-left"
      onClick={handleOutsideClick}
    >
      <div
        className="bg-white dark:bg-gray-800 p-6 rounded-lg max-w-2xl w-full m-4 animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="float-right font-bold text-2xl dark:text-white hover:scale-110 transition-transform"
        >
          &times;
        </button>
        <h2 className="text-2xl font-bold mb-4 dark:text-white">
          {project.title}
        </h2>
        <p className="mb-4 dark:text-gray-300">{project.fullDescription}</p>
        <p className="text-gray-600 dark:text-gray-400 mb-4">{project.date}</p>
        {project.image && (
          <div className="mb-4">
            <img
              src={project.image}
              alt={project.title}
              className="w-full h-auto rounded-lg shadow-md"
            />
          </div>
        )}
        <ul className="list-disc pl-5 mb-4 dark:text-gray-300">
          {project.bulletPoints.map((point, index) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
        <div className="flex justify-end space-x-4">
          {project.github && (
            <a
              href={project.github}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white transition-all hover:scale-110"
            >
              <FontAwesomeIcon icon={faGithub} size="2x" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const Projects = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [ref, isVisible] = useScrollReveal();

  const projects = [
    {
      title: "ForumAI",
      image: forum,
      date: "Sep 2024 - April 2025",
      shortDescription:
        "ForumAI combines AI capabilities with structured academic forums, enabling students and faculty to collaborate in a controlled environment where AI assists learning while maintaining academic integrity.",
      bulletPoints: [
        "Co-lead of a team of 14 members in developing ForumAI, an AI-powered academic forum that enhances structured discussions and document interaction",
        "Designed and implemented advanced AI features, integrating Retrieval Augmented Generation (RAG) to optimize knowledge retrieval and improve student engagement",
      ],
      github: "https://github.com/ubclaunchpad/forum",
    },
    {
      title: "Portfolio Diversification Model",
      image: portfolioDiversificationModel,
      date: "Oct 2023 - Dec 2023",
      shortDescription:
        "Reinforcement Learning algorithms for financial data analysis and forecasting",
      bulletPoints: [
        "Employed the Upper Confidence Bound (UCB) algorithm using Python to evaluate stock performance and generate investment recommendations tailored to user-defined risk tolerance and historical performance",
        "Helped with the development of the Keras Long Short-Term Memory (LSTM) model for time series forecasting, a critical feature to enhance the project's predictive capabilities",
        "Worked on the integration of the Vector Auto Regression (VAR) model for financial trend forecasting, enabling the system to provide insights into future market movements",
      ],
      github: "https://github.com/armanmoztar/Portfolio-Diversification-Model",
    },
    {
      title: "BC Cancer Ticket Management System",
      image: labby,
      date: "Oct 2022 - Apr 2023",
      shortDescription: "Ticket management system for BC Cancer stakeholders",
      bulletPoints: [
        "Developed a laboratory management system as part of UBC Launch Pad and actively met with organizational stakeholders",
        "Developed routes and models to load questions into SQL database",
        "Wrote unit tests for question routes using Jest",
        "Implemented Load and Delete questions saga using Redux and Axios Library",
      ],
      github: "https://github.com/ubclaunchpad/labby",
    },
    {
      title: "Posture Fix",
      image: postureFix,
      date: "Jan 2022 - Feb 2022",
      shortDescription:
        "Chrome extension to alert users to adjust their posture",
      bulletPoints: [
        "Published and developed a chrome extension that alerts users to adjust their posture while they work",
        "Utilized JavaScript and CSS to create a user-friendly interface that provides feedback on posture",
        "Implemented a timer to alert users to take a break and fix their posture every 30 minutes",
      ],
      github: "https://github.com/armanmoztar/posture-fix",
    },
  ];

  return (
    <div
      ref={ref}
      className={`mb-8 transition-all duration-500 ${
        isVisible ? "animate-slide-up" : "opacity-0"
      }`}
    >
      <h2 className="text-2xl font-bold mb-4 text-left dark:text-white">
        Projects
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((project, index) => (
          <ProjectCard
            key={index}
            project={project}
            onClick={() => setSelectedProject(project)}
          />
        ))}
      </div>
      {selectedProject && (
        <ProjectModal
          project={selectedProject}
          onClose={() => setSelectedProject(null)}
        />
      )}
    </div>
  );
};

const Portfolio = () => {
  const [isDark, setIsDark] = useDarkMode();

  return (
    <div className="min-h-screen relative overflow-hidden dark:bg-gray-900 dark-mode-transition">
      <DarkModeToggle isDark={isDark} setIsDark={setIsDark} />

      {/* Content container */}
      <div className="relative z-10 container mx-auto p-8">
        <ProfileHeader profilePicture={ProfilePicture} />
        <AboutMe />
        <TravelPreview />
        <Experience />
        <Projects />
      </div>

      {/* Background container */}
      <div className="background-container"></div>
    </div>
  );
};

export default Portfolio;
